import { ActionBar } from "$components/action-bar/action-bar";
import { useRegisterActionBar } from "$components/action-bar/action-bar-offset";
import { ActionBarSpacer } from "$components/action-bar/action-bar-spacer";
import type { ActionItem } from "$components/action-bar/action-item";
import { CheckIcon, EditIcon } from "$components/icons";
import { Page } from "$components/page";
import { toast } from "$global-providers/toast-provider";
import { useUser } from "$global-providers/user-provider";
import {
  AllStockTakeLotFieldsFragment,
  mapStockTakeLotsFragmentToRows,
} from "$routes/inventory/_lib/stock-take-lots-fragment";
import StockTakeProgressBanner from "$routes/inventory/_lib/stock-take-progress-banner";
import StockTakeSummaryTable from "$routes/inventory/_lib/stock-take-summary-table";
import { useFacilityLocationName } from "$routes/inventory/_lib/use-facility-locations";
import { linkToInventory, linkToStockTake } from "$routes/inventory/links";
import { gql } from "@apollo/client";
import { Box, Typography } from "@material-ui/core";
import { StockTakeLotsSearchOrder } from "elephant-web/gql";
import { useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import {
  useFinaliseStockTakeMutation,
  useStockTakeReviewQuery,
} from "./__generated__/page.gql";

const PAGE_SIZE = 20;

gql`
  ${AllStockTakeLotFieldsFragment}

  query stockTakeReview(
    $stockTakeId: ID!
    $facilityId: ID!
    $input: StockTakeLotsInput
    $options: StockTakeLotsOptions
  ) {
    stockTake(input: { stockTakeId: $stockTakeId, facilityId: $facilityId }) {
      locationId
      lots(input: $input, options: $options) {
        items {
          ...AllStockTakeLotFields
        }
      }
      aggregateInfo {
        totalLotCount
        reviewedLotCount
        totalNewLotsAdded
        totalQuantityAdded
        totalQuantityRemoved
      }
    }
  }
`;

const StockTakeReviewPage = (): JSX.Element => {
  const { stockTakeId } = useParams<{ stockTakeId: string }>();
  const { facilityId } = useUser();
  const [currentPage, setCurrentPage] = useState<number>(0);
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const actionBarRef = useRef<HTMLElement>(null);
  useRegisterActionBar(actionBarRef);

  const [finaliseStockTakeMutation] = useFinaliseStockTakeMutation({
    onCompleted: () => {
      toast.success(
        formatMessage({
          id: "inventory.stockTake.review.success",
          defaultMessage: "Successfully completed stocktake",
        }),
      );
      navigate(linkToInventory());
    },
    onError: (error) => {
      const errorMessage = formatMessage(
        {
          id: "inventory.stockTake.review.finaliseError",
          defaultMessage: "Failed to finalise stocktake: {errorMessage}",
        },
        {
          errorMessage: error.message,
        },
      );
      toast.error(errorMessage);
    },
  });

  const { data, loading } = useStockTakeReviewQuery({
    variables: {
      stockTakeId,
      facilityId,
      input: {
        reviewed: true,
      },
      options: {
        orderBy: StockTakeLotsSearchOrder.QuantityDifference,
        pageNumber: currentPage,
        pageSize: PAGE_SIZE,
      },
    },
  });

  const locationName = useFacilityLocationName(data?.stockTake?.locationId);

  const { totalLotCount, reviewedLotCount } =
    data?.stockTake?.aggregateInfo || {};
  const stockTakeRows = mapStockTakeLotsFragmentToRows(
    data?.stockTake?.lots.items ?? [],
  );

  const finaliseDisabled = (reviewedLotCount || 0) === 0;

  const actions: ActionItem[] = [
    {
      id: "inventory.stockTake.review.header.edit",
      defaultMessage: "Edit",
      icon: <EditIcon />,
      onClick: () => navigate(linkToStockTake(stockTakeId)),
    },
    {
      id: "inventory.stockTake.review.header.finalise",
      defaultMessage: "Finalise",
      variant: "primary",
      icon: <CheckIcon />,
      disabled: finaliseDisabled,
      onClick: () =>
        finaliseStockTakeMutation({
          variables: { input: { facilityId, stockTakeId } },
        }),
    },
  ];

  return (
    <>
      <StockTakeProgressBanner
        loading={loading}
        totalCount={totalLotCount}
        reviewedCount={reviewedLotCount}
      />
      <Page data-dense>
        <Box display="flex" marginTop={2} marginBottom={3} alignItems="center">
          <Box marginRight={2}>
            <Typography variant="h3">
              <FormattedMessage
                id="inventory.stockTake.summary.title"
                defaultMessage="Stocktake Summary"
              />
            </Typography>
          </Box>
          <Typography variant="body1">{locationName}</Typography>
        </Box>
        <StockTakeSummaryTable
          loading={loading}
          stockTakeRows={stockTakeRows}
          totalRows={totalLotCount}
          currentPage={currentPage}
          pageSize={PAGE_SIZE}
          onPageChange={setCurrentPage}
        />
        <ActionBarSpacer />
      </Page>
      <ActionBar actions={actions} containerRef={actionBarRef} />
    </>
  );
};

gql`
  mutation finaliseStockTake($input: FinaliseStockTakeInput!) {
    finaliseStockTake(input: $input) {
      id
      aggregateInfo {
        totalLotCount
        reviewedLotCount
        totalNewLotsAdded
        totalQuantityAdded
        totalQuantityRemoved
      }
    }
  }
`;

export default StockTakeReviewPage;
